/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import { isEmpty, isEqual } from 'lodash';
import clsx from 'clsx';
import Button from 'carbon-react/lib/components/button';
import Icon from 'carbon-react/lib/components/icon';

import { FormattedMessage } from '@futrli/localisation';
import { PreviewAssumptionImpactsResponse, UnitAssumptionPriceVariant } from '@futrli-sdk/predict';

import {
  DateState,
  ErrorState,
  FormState,
  OwnState,
  PriceVariant,
  PriceVariantErrorState,
  StringState,
  RepeatPattern
} from './types';
import { calculateDateChanges, formatPendingData, getNextDay, isValidNumberValue, validate } from './helpers';
import styles from './unit.scss';
import { FormulaError, PredictionHeaderProps } from '../../types';
import { forceLocalDate } from '../../../../utils/dates';
import { AnyObject } from '@futrli/shared-services';
import { PriceVariantComponent } from './price-variant';

interface UnitProps {
  currency?: string | null;
  initialPriceVariants: PriceVariant[];
  isFormSubmitted: boolean;
  onChange: (isValid: boolean, data: AnyObject) => void;
  PredictionHeader: React.FunctionComponent<PredictionHeaderProps>;
  previewImpacts: PreviewAssumptionImpactsResponse | undefined;
}

export interface UnitMethodData {
  price_variants: PriceVariant[];
}

export const defaultPriceVariant: Readonly<PriceVariant> = {
  end_date: null,
  growth: '0',
  quantity: '',
  refund: '0',
  repeat_pattern: 'monthly',
  selling_price: '',
  start_date: new Date(Date.now())
};

export const invalidNumberMessage = (
  <FormattedMessage id="common.errorMsg.invalidNumber" defaultMessage="Must be a valid number" />
);
export const missingEndDateMessage = (
  <FormattedMessage
    id="predict.price_changes_require_end_date"
    defaultMessage="Price changes require a specific end date"
  />
);
export const invalidSellingPriceMessage = (
  <FormattedMessage
    id="predict.enter_valid_selling_price"
    defaultMessage="Please enter a valid selling price and try again"
  />
);

export const invalidQuantityMessage = (
  <FormattedMessage
    id="predict.enter_valid_unites_number"
    defaultMessage="Please enter the number of units and try again"
  />
);

const maxPriceVariants = 3;

export const getInitialPriceVariant = (
  predictionDetails?: UnitAssumptionPriceVariant[],
  forecastFromDate?: string | null
) => {
  if (predictionDetails?.length) {
    return predictionDetails.map((priceVariant: UnitAssumptionPriceVariant) => ({
      selling_price: priceVariant.selling_price,
      quantity: priceVariant.quantity.toString(),
      repeat_pattern: priceVariant.repeat_pattern as RepeatPattern,
      growth: priceVariant.growth,
      refund: priceVariant.refund,
      start_date: forceLocalDate(priceVariant.start_date),
      end_date: priceVariant.end_date ? forceLocalDate(priceVariant.end_date) : null
    }));
  }

  const newDefaultPriceVariants = {
    ...defaultPriceVariant
  };

  if (forecastFromDate) {
    newDefaultPriceVariants.start_date = forceLocalDate(forecastFromDate);
  }
  return [
    {
      ...newDefaultPriceVariants
    }
  ];
};

export const UnitMethod = ({
  currency,
  initialPriceVariants,
  isFormSubmitted,
  onChange,
  PredictionHeader,
  previewImpacts
}: UnitProps) => {
  const [errors, setErrors] = useState<ErrorState>();
  const [price_variant_errors, setPriceVariantErrors] = useState<PriceVariantErrorState<PriceVariant>>({});
  const [missing_end_date_for_last_variant, setMissingEndDateForLastVariant] = useState(false);
  const [price_variants, setPriceVariants] = useState<PriceVariant[]>(initialPriceVariants);

  useEffect(() => {
    if (!isEqual(initialPriceVariants, price_variants)) {
      setPriceVariants(initialPriceVariants);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPriceVariants]);

  useEffect(() => {
    onChange(
      isEmpty(validate(price_variants, missing_end_date_for_last_variant, setMissingEndDateForLastVariant).errors),
      formatPendingData(price_variants)
    );
  }, []); // eslint-disable-line

  useEffect(() => {
    const formErrors = validate(price_variants, missing_end_date_for_last_variant, setMissingEndDateForLastVariant);

    if (isFormSubmitted) {
      setFormErrorState(formErrors);
    }
  }, [isFormSubmitted]); // eslint-disable-line

  useEffect(() => {
    const formErrors = validate(price_variants, missing_end_date_for_last_variant, setMissingEndDateForLastVariant);

    if (isFormSubmitted) {
      setFormErrorState(formErrors);
    }

    onChange(isEmpty(formErrors.errors), formatPendingData(price_variants));
  }, [price_variants]); // eslint-disable-line

  const setFormErrorState = (formErrors: FormState) => {
    setErrors(formErrors.errors);
    formErrors.missing_end_date_for_last_variant &&
      setMissingEndDateForLastVariant(formErrors.missing_end_date_for_last_variant);
    setPriceVariantErrors(formErrors.price_variant_errors);
  };

  const handleOnNumberChange = (name: keyof StringState, index: number) => (value: string): void => {
    if (name === 'growth' || name === 'refund') {
      const newPriceVariants = price_variants.map(price_variant => {
        return {
          ...price_variant,
          [name]: value
        };
      });

      setPriceVariants(newPriceVariants);
      return;
    }

    const priceVariant = {
      ...price_variants[index],
      [name]: value
    };
    const new_price_variants = Object.assign([], price_variants, { [index]: priceVariant });

    setPriceVariants(new_price_variants);
  };

  const hasError = (fieldName: keyof OwnState, index?: number, subFieldName?: keyof PriceVariant): boolean => {
    return getError(fieldName, index, subFieldName) !== null;
  };

  function getError(fieldName: keyof OwnState, index?: number, subFieldName?: keyof PriceVariant): JSX.Element | null {
    if (fieldName === 'price_variants') {
      return (
        (index !== undefined &&
          subFieldName &&
          errors &&
          errors.price_variants &&
          errors.price_variants[index] &&
          errors.price_variants[index][subFieldName]) ||
        null
      );
    }

    return (errors && errors[fieldName]) || null;
  }

  const removeAnyErrorFixedOnBlur = (name: keyof StringState, index: number) => {
    const newPriceVariantErrors = { ...price_variant_errors };

    if (errors?.price_variants?.[index]?.[name]) {
      const priceVariants = { ...errors.price_variants };
      delete priceVariants[index][name];
      let any_errors = false;

      for (let i = 0; i < maxPriceVariants; i++) {
        if (priceVariants[i]?.[name]) {
          any_errors = true;
        }
      }

      if (!any_errors) {
        delete newPriceVariantErrors[name];
      }

      setPriceVariantErrors(newPriceVariantErrors);
      setErrors(Object.assign({}, { price_variants: priceVariants }));
    }
  };

  const handleOnBlur = (name: keyof StringState, index: number) => (value: string): void => {
    if (!isValidNumberValue(name, value)) {
      if (errors?.price_variants?.[index]?.[name]) {
        return;
      }

      const newPriceVariantErrors = { ...price_variant_errors };
      const priceVariants = (errors && errors.price_variants && { ...errors.price_variants }) || [{}, {}, {}];

      switch (name) {
        case 'selling_price':
          priceVariants[index].selling_price = invalidSellingPriceMessage;
          newPriceVariantErrors.selling_price = invalidSellingPriceMessage;
          break;
        case 'quantity':
          priceVariants[index].quantity = invalidQuantityMessage;
          newPriceVariantErrors.quantity = invalidQuantityMessage;
          break;
        default:
          priceVariants[index][name] = invalidNumberMessage;
          newPriceVariantErrors[name] = invalidNumberMessage;
      }

      setPriceVariantErrors(newPriceVariantErrors);
      setErrors(Object.assign({}, { price_variants: priceVariants }));
    } else {
      removeAnyErrorFixedOnBlur(name, index);
    }
  };

  const handleDateChange = (name: keyof DateState, index: number) => (date: Date) => {
    const priceVariantUpdates = calculateDateChanges(name, index, date, price_variants);
    const new_price_variants = Object.assign([], price_variants, ...priceVariantUpdates);

    setPriceVariants(new_price_variants);
  };

  const handleDateClear = (name: keyof DateState, index: number) => (): void => {
    const priceVariant = {
      ...price_variants[index],
      [name]: defaultPriceVariant[name]
    };
    const new_price_variants = Object.assign([], price_variants, { [index]: priceVariant });

    setPriceVariants(new_price_variants);
  };

  const handleOnChange = (name: keyof StringState) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newPriceVariants = price_variants.map(price_variant => {
      return {
        ...price_variant,
        [name]: event.target.value
      };
    });

    setPriceVariants(newPriceVariants);
  };

  const handleAddPriceChange = () => {
    const lastIndex = price_variants.length - 1;

    validate(price_variants, missing_end_date_for_last_variant, setMissingEndDateForLastVariant, true);

    if (price_variants[lastIndex].end_date === null) {
      return;
    }

    const newPriceVariant = {
      ...defaultPriceVariant,
      start_date: getNextDay(price_variants[price_variants.length - 1].end_date as Date),
      repeat_pattern: price_variants[0].repeat_pattern,
      growth: price_variants[0].growth,
      refund: price_variants[0].refund
    };

    setPriceVariants([...price_variants, { ...newPriceVariant }]);
  };

  const handleRemovePriceChange = () => {
    const newPriceVariants = [...price_variants].slice(0, -1);

    setPriceVariants(newPriceVariants);
  };

  const showErrors = !isEmpty(price_variant_errors) || missing_end_date_for_last_variant;

  return (
    <div>
      <PredictionHeader />
      <div className={styles.container}>
        {price_variants.map((price_variant, index) => (
          <PriceVariantComponent
            key={index}
            currency={currency!}
            price_variant={price_variant}
            missing_end_date_for_last_variant={missing_end_date_for_last_variant}
            index={index}
            clearable={index === price_variants.length - 1}
            handleOnNumberChange={handleOnNumberChange}
            hasError={hasError}
            handleOnBlur={handleOnBlur}
            handleDateChange={handleDateChange}
            handleDateClear={handleDateClear}
            handleOnChange={handleOnChange}
          />
        ))}
        {showErrors ? (
          <div className={styles.price_variant_errors}>
            {Object.values(price_variant_errors).map((errorMsg, index) => {
              return (
                <div key={`error_${index}`} className={clsx('tag is-danger is-light', styles.errorRow)}>
                  {errorMsg}
                </div>
              );
            })}
            {missing_end_date_for_last_variant && (
              <div className={clsx('tag is-danger is-light', styles.errorRow)}>{missingEndDateMessage}</div>
            )}
          </div>
        ) : null}
        {price_variants.length < maxPriceVariants && (
          <Button onClick={handleAddPriceChange} mr="20px" data-testid="add-a-price-change-btn">
            <div>
              <Icon type="add" />
            </div>
            <FormattedMessage id="predict.add_a_price_change" defaultMessage="Add a price change" />
          </Button>
        )}
        {price_variants.length > 1 && (
          <Button destructive onClick={handleRemovePriceChange} data-testid="remove-last-price-change-btn">
            <div>
              <Icon type="remove" />
            </div>
            <FormattedMessage id="predict.remove_last_price_change" defaultMessage="Remove last price change" />
          </Button>
        )}
      </div>
      {!previewImpacts?.is_valid && previewImpacts?.error_code === FormulaError.ERROR_VALUES_OUT_OF_EXPECTED_RANGE && (
        <div className={styles.formulaError}>
          <FormattedMessage
            id="predict.predict_error_values_out_of_expected_range"
            defaultMessage="Prediction cannot be saved, as it produces values out of expected range."
          />
        </div>
      )}
    </div>
  );
};
